import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Badge, BadgeList, TagBadge } from "../../components/badge";
import { BigTextPreview } from "../../components/big-text-preview";
import { Button, ButtonRow, PrimaryButton } from "../../components/button";
import { Card, CardContent } from "../../components/card";
import { CodeEditor } from "../../components/code-editor";
import { CollectionPickerControl } from "../../components/collection-picker";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DataPickerControl, MultiDataPickerControl } from "../../components/data-picker";
import { Dropdown, DropdownDivider, DropdownItem } from "../../components/dropdown";
import { Editor, EditorDescription, EditorRow, EditorTitle } from "../../components/editor";
import { NumberInput, Select, TextArea, TextInput } from "../../components/form";
import { SpecializedControl } from "../../components/specializedObject";
import { Icon } from "../../components/icon";
import { Image, ImageContainer } from "../../components/image";
import { JsonControl } from "../../components/json-control";
import { Content, Section } from "../../components/layout";
import { ButtonLink, ExternalLink, Link } from "../../components/link";
import { ListPickerControl } from "../../components/list-picker";
import { ModelPickerControl } from "../../components/model-picker";
import { TabControl } from "../../components/tabcontrol";
import { BlockCodeWrapper, CheckWrapper, DashboardTextHeading, Heading, InlineCode, LightTitle, MultilingualCodeWrapper, MutedText, Paragraph, ParagraphGroup, TextHeading } from "../../components/text";
import { Information, Warning } from "../../components/warn";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { copyToClipboard } from "../../ui/clipboard";
import { formatFloat, formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { showSuccessToast } from "../../ui/toast";
import { ellipsis, find, identifierName, valueFrom } from "../../util";
import { GatewayPipeline } from "../../components/gateway-pipeline";
import * as IntervalManager from "../../interval-manager";
import { ModelBadge } from "../../components/model-icon";
import { SkillPickerControl } from "../../components/skill-picker";
import { Meter } from "../../components/meter";
import { requestAiEdit } from "../../ui/ai-edit";
import { acceptDiff } from "../../ui/accept-diff";
import { Constants } from "../../constants";
import { getSession } from "../../session";
import { ToolNamePicker } from "../../components/tool-name-picker";
import { getDefaultReranker, getReranker, loadRerankers } from "../../rerankers";
import { PickImageGenModel, PickRerankerModel } from "../../helpers/model-helpers";

export default async function (ctx) {

    const gatewayId = ctx.params.gatewayId;
    const isCreatingGateway = gatewayId == "create";
    const rerankers = await loadRerankers();
    const defaultReranker = getDefaultReranker(rerankers);

    const gatewayPipelinePreviewState = el.state.create(null);
    const gateway = el.state.create(null);
    const isIntegratedModel = el.state.pooling(() => valueFrom("gatewayBaseAddress") == "@integrated", {
        interval: 100
    });
    const isComplexityRoutingModel = el.state.pooling(() => valueFrom("gatewayModelName") == "@model-router/complexity", {
        interval: 100
    })

    IntervalManager.setManagedInterval(() => {
        var current = getGatewayData();
        if (JSON.stringify(gatewayPipelinePreviewState.value) != JSON.stringify(current)) {
            gatewayPipelinePreviewState.value = current;
        }
    }, 500);

    function updateView() {
        runOperation("Fetching gateway...", async () => {
            const response = await api.getJson(`/api/v1/ai-gateways/${gatewayId}`);
            gateway.value = response.data;
            gatewayPipelinePreviewState.value = getGatewayData();
        });
    }

    if (!isCreatingGateway) {
        updateView();
    }

    function getGatewayData() {
        return {
            name: valueFrom("gatewayName"),
            parameters: {
                baseAddress: valueFrom("gatewayBaseAddress"),
                modelName: valueFrom("gatewayModelName"),
                apiKey: valueFrom("gatewayApiKey"),
                transportInterface: valueFrom("gatewayTransportInterface"),
                routingOption: valueFrom("gatewayRoutingOption"),
                allowedProviders: valueFrom("gatewayAllowedProviders") ?? ["*"],

                knowledgeCollections: valueFrom("gatewayCollections"),
                queryStrategy: valueFrom("gatewayQueryStrategy"),
                rerankerName: valueFrom("gatewayRerankerName"),
                knowledgeBaseMaximumResults: valueFrom("gatewayRetrievalMaximumCount"),
                knowledgeBaseMinimumScore: valueFrom("gatewayRetrievalMinimumScore"),
                knowledgeUseMetaDescriptions: valueFrom("gatewayKnowledgeUseMetaDescriptions"),

                queryStrategyParameters: {
                    concatenateContextSize: valueFrom("gatewayConcatenateSize"),
                    rewriteContextSize: valueFrom("gatewayRewriteSize"),
                },

                systemInstruction: valueFrom("gatewaySystemInstructions")?.nullWhenEmpty(),
                systemInstructionsSources: valueFrom("gatewaySystemInstructionSources"),
                userPromptTemplate: valueFrom("gatewayPromptTemplate")?.nullWhenEmpty(),
                assistantPrefill: valueFrom("gatewayAssistantPrefill")?.nullWhenEmpty(),
                stop: valueFrom("gatewayStopSentence")?.nullWhenEmpty(),
                includePrefillingInMessages: valueFrom("gatewayIncludePrefillingParameters"),

                temperature: valueFrom("gatewayTemperature"),
                topP: valueFrom("gatewayTopP"),
                presencePenalty: valueFrom("gatewayPresencePenalty"),
                frequencyPenalty: valueFrom("gatewayFrequencyPenalty"),
                maxCompletionTokens: valueFrom("gatewayMaxCompletionTokens"),
                reasoningEffort: valueFrom("gatewayReasoningEffort"),
                verbosity: valueFrom("gatewayVerbosity"),

                mcpSources: valueFrom("gatewayMcpSources"),
                protocolFunctionSources: valueFrom("gatewayProtocolFunctionSources"),
                protocolFunctions: valueFrom("gatewayProtocolFunctions"),
                tools: valueFrom("gatewayTools"),

                skills: valueFrom("gatewaySkills"),
                hideToolsWithoutSkill: valueFrom("gatewayHideToolsWithoutSkills"),
                alwaysVisibleTools: valueFrom("gatewayAlwaysVisibleTools") || [],

                enableBash: valueFrom("gatewayEnableBash") || false,
                includeWorkspaceInstructions: valueFrom("gatewayIncludeWorkspaceInstructions") ?? true,
                bashOptions: {
                    AllowDataPersistence: valueFrom("gatewayAllowBashDataPersistency") || false,
                    ToolExclusionMode: valueFrom("gatewayBashToolExclusionMode"),
                    ToolList: valueFrom("gatewayBashToolList") || [],
                },

                flags: valueFrom("gatewayFlags"),

                multimodalResolverParameters: Object.fromEntries(
                    Object.entries(valueFrom("gatewayMultimodalResolverParameters") ?? {}).filter(([, engine]) => engine)
                ),

                contextOverflowAction: valueFrom("gatewayContextOverflowAction"),
                contextMaximumSize: valueFrom("gatewayContextMaximumSize"),

                knownToolHandlerName: valueFrom("gatewayToolHandler"),
                toolContextCount: valueFrom("gatewayToolContextSize"),
                toolInvocationExplanations: valueFrom("gatewayToolExplanations") || false,

                workerScriptSource: valueFrom("gatewayWorkerScriptSource")?.nullWhenEmpty(),

                sentinelOptions: {
                    enabledFunctions: valueFrom("gatewaySentinelEnabledFunctions")
                },

                moderationParameters: {
                    mode: valueFrom("modMode"),
                    violenceThreshold: valueFrom("modViolenceThreshold") ?? 0,
                    sexualExplicitThreshold: valueFrom("modSexualExplicitThreshold") ?? 0,
                    politicalThreshold: valueFrom("modPoliticalThreshold") ?? 0,
                    dangerousContentThreshold: valueFrom("modDangerousContentThreshold") ?? 0,
                    jailbreakThreshold: valueFrom("modJailbreakThreshold") ?? 0,
                    additionalRules: valueFrom("modAdditionalRules")?.nullWhenEmpty()
                },

                timeZone: valueFrom("gatewayTimeZone") ?? "America/Sao_Paulo",

                builtinFunctionsOptions: {
                    webSearchMode: valueFrom("gatewayBuiltinWebSearchMode"),
                    webSearchMaxResults: valueFrom("gatewayBuiltinWebSearchMaxResults"),
                    imageGenerationQuality: valueFrom("gatewayBuiltinImageGenQuality"),
                    imageGenerationMaxResults: valueFrom("gatewayBuiltinImageGenMaxImages"),
                    imageGenerationAllowMatureContent: valueFrom("gatewayBuiltinImageGenAllowMatureContent"),
                    ImageGenerationModelName: valueFrom("gatewayBuiltinImageGenModelName"),
                    AllowSharedMemory: valueFrom("gatewayBuiltinAllowSharedMemory"),
                },

                additionalSettings: valueFrom("gatewayAdditionalSettings"),

                modelRoutingParameters: isComplexityRoutingModel.value && {
                    lowComplexityModelName: valueFrom("gatewayModelRouterLowComplexityModelName"),
                    lowComplexityModelReasoningEffort: valueFrom("gatewayModelRouterLowComplexityReasoningEffort"),

                    mediumComplexityModelName: valueFrom("gatewayModelRouterMediumComplexityModelName"),
                    mediumComplexityModelReasoningEffort: valueFrom("gatewayModelRouterMediumComplexityReasoningEffort"),

                    highComplexityModelName: valueFrom("gatewayModelRouterHighComplexityModelName"),
                    highComplexityModelReasoningEffort: valueFrom("gatewayModelRouterHighComplexityReasoningEffort"),
                } || null
            }
        };
    }

    function handleSave() {
        const data = getGatewayData();

        runOperation("Saving gateway...", async () => {
            if (isCreatingGateway) {
                const response = await api.postJson("/api/v1/ai-gateways", data);
                page(`/dashboard/gateways/${response.data.aiGatewayId}`);
            } else {
                await api.patchJson(`/api/v1/ai-gateways/${gatewayId}`, data);
            }
        });
    }

    return el.state.listening([gateway], () =>
        Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                    onClick() {
                        history.go(-1);
                        page();
                    }
                })
            ),
            DashboardTitle(isCreatingGateway ? "New AI gateway" :
                DashboardTextHeading(
                    el.fragment(gateway.value?.name, MutedText(" ~#", gatewayId.slice(-6))), "AI Gateway details")),
            Section(
                CommandBar(
                    Button(
                        Icon("save-line"),
                        isCreatingGateway ? "Create AI gateway" : "Save changes",
                        {
                            onClick: handleSave
                        }
                    ),
                    !isCreatingGateway && Dropdown("Options",
                        DropdownItem("View integration code", Icon("braces-line"), {
                            onClick() {
                                displayAlert({
                                    title: "Gateway code",
                                    dialogSize: "medium",
                                    message: el.fragment(
                                        "Use the code below to integrate your AI gateway.",
                                        MultilingualCodeWrapper("Example", [
                                            {
                                                label: "Python",
                                                language: "python",
                                                code: `
import os
from openai import OpenAI

client = OpenAI(
    base_url="${window.env.apiEndpoint}v1",
    api_key=os.environ["AIVAX_API_KEY"],
)

completion = client.chat.completions.create(
    model="${gateway.value?.slug}", # or ${gatewayId}
    messages=[
        {
            "role": "user",
            "content": "What is the capital of France?"
        }
    ],
)

print(completion.choices[0].message)
                                                    `
                                            },
                                            {
                                                label: "JavaScript",
                                                language: "javascript",
                                                code: `
import { OpenAI } from "openai";

const client = new OpenAI({
	baseURL: "${window.env.apiEndpoint}v1",
	apiKey: process.env.AIVAX_API_KEY,
});

const chatCompletion = await client.chat.completions.create({
	model: "${gateway.value?.slug}", // or ${gatewayId}
    messages: [
        {
            role: "user",
            content: "What is the capital of France?",
        },
    ],
});

console.log(chatCompletion.choices[0].message);
                                                    `
                                            },
                                            {
                                                label: "Curl",
                                                language: "bash",
                                                code: `
curl ${window.env.apiEndpoint}v1/chat/completions \\
    -H "Authorization: Bearer $AIVAX_API_KEY" \\
    -H 'Content-Type: application/json' \\
    -d '{
        "messages": [
            {
                "role": "user",
                "content": "What is the capital of France?"
            }
        ],
        "model": "${gateway.value?.slug}",
        "stream": false
    }'
                                                    `
                                            }
                                        ])
                                    )
                                })
                            }
                        }),
                        DropdownItem("View MCP code", Icon("braces-line"), {
                            onClick() {
                                displayAlert({
                                    title: "MCP code",
                                    dialogSize: "medium",
                                    message: el.fragment(
                                        "Use the code below to integrate your AI model as a MCP source.",
                                        MultilingualCodeWrapper("Example", [
                                            {
                                                label: "VSCode mcp.json",
                                                language: "json",
                                                code: `
{
  "servers": {
    "${identifierName(gateway.value?.name)}": {
      "type": "http",
      "url": "${window.env.apiEndpoint}v1/mcp/inference",
      "headers": {
        // required. specifies your api key.
        "Authorization": "Bearer ${getSession()?.sessionKey}",

        // required. can be the model slug or the gateway ID
        "X-Mcp-Model-Name": "${gateway.value?.slug}",

        // optional. choose a custom tool name for your editor
        "X-Mcp-Tool-Name": "my_model",

        // optional. a description that will be shown in the UI for this MCP tool
        "X-Mcp-Tool-Description": "My custom model for inference",

        // optional. the mcp tool title.
        "X-Mcp-Tool-Title": "Invoke my AI gateway",

        // optional. specifies the "user" value that will be sent to the model chat completions.
        "X-Mcp-User": "abc123"
      }
    }
  }
}

                                                    `
                                            }
                                        ])
                                    )
                                })
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Run in playground", Icon("chat-1-line"), {
                            onClick() {
                                const params = new URLSearchParams({
                                    "api-endpoint": window.env.apiEndpoint + "v1/chat/completions",
                                    "api-model": gateway.value?.slug,
                                    "api-key": getSession()?.sessionKey
                                });
                                const playgroundUrl = `https://playground.aivax.net/?${params.toString()}`;
                                window.open(playgroundUrl, "_blank");
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Copy model slug", Icon("file-copy-line"), {
                            onClick() {
                                copyToClipboard(gateway.value?.slug);
                            }
                        }),
                        DropdownItem("Copy ID", Icon("file-copy-line"), {
                            onClick() {
                                copyToClipboard(gatewayId);
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Import from JSON", Icon("download-2-line"), {
                            onClick() {
                                displayFormControl({
                                    title: "Import gateway from a JSON",
                                    message: el.fragment(
                                        Paragraph("Enter the gateway JSON you wish to import below to import all its properties and parameters."),
                                        Paragraph("All current parameters will be overwritten by the provided JSON.")
                                    ),
                                    dialogSize: "large",
                                    formControlItem: JsonControl({ value: "" }),
                                    onConfirm(value) {
                                        gateway.value = value;
                                    },
                                    validate: []
                                })
                            }
                        }),
                        DropdownItem("Export to JSON", Icon("upload-2-line"), {
                            onClick() {
                                displayAlert({
                                    title: "Export gateway as JSON",
                                    dialogSize: "large",
                                    message: el.fragment(
                                        Paragraph("The JSON code below contains everything defined in your gateway."),
                                        Paragraph("Copy it and save it somewhere."),
                                        BlockCodeWrapper("gateway.json", JSON.stringify(getGatewayData(), null, 4), "json")
                                    )
                                })
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("View conversation logs", Icon("dropdown-list"), {
                            onClick() {
                                page(`/dashboard/conversations?${new URLSearchParams({
                                    queryFilter: `--gateway ${gatewayId}`
                                }).toString()}`);
                            }
                        }),
                        DropdownItem("View Agentic Tests", Icon("chat-check-line"), {
                            onClick() {
                                page(`/dashboard/tests?${new URLSearchParams({ gateway: gatewayId }).toString()}`);
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Delete gateway", Icon("delete-bin-line"), {
                            onClick() {
                                displayAlert({
                                    title: "Delete AI gateway",
                                    message: "Are you sure you want to delete this AI gateway? This action cannot be undone.",
                                    type: "alert-warning",
                                    buttons: [
                                        {
                                            label: "Delete AI gateway",
                                            onClick(event) {
                                                runOperation("Deleting AI gateway...", async () => {
                                                    await api.deleteJson(`/api/v1/ai-gateways/${gatewayId}`);
                                                    history.go(-1);
                                                    page();
                                                });
                                            }
                                        },
                                        {
                                            label: "Cancel",
                                            onClick(event) {
                                                ;
                                            }
                                        }
                                    ]
                                })
                            }
                        })
                    )
                )
            ),
            Section(
                TabControl([
                    {
                        title: "Gateway",
                        icon: Icon("share-forward-line"),
                        contents: el.fragment(
                            Heading(
                                "Configure your AI gateway",
                                "Set up the basic configuration for your AI gateway, including name and pipeline structure."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayName",
                                        value: gateway.value?.name,
                                        renderValue(value) {
                                            return Button(value || "No name");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Gateway name", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.length > 3, "The gateway name must be at least 3 characters long."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Gateway name"),
                                        Paragraph("Specifies a friendly name for your AI gateway.")
                                    )
                                ),
                            ),
                            Section(
                                el.state.listening([gatewayPipelinePreviewState], () =>
                                    GatewayPipeline(getGatewayData(), rerankers)
                                )
                            )
                        )
                    },
                    {
                        title: "Inference",
                        icon: Icon("instance-line"),
                        contents: el.fragment(
                            Heading(
                                "Configure inference settings",
                                "Choose the AI model and configure how your gateway will communicate with the inference provider."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayModelName",
                                        value: gateway.value?.parameters.modelName,
                                        renderValue(value) {
                                            return value && ModelBadge(value) || Warning("Not selected");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Model name", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.length > 0, "Model name cannot be empty"],
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Gateway model name"),
                                        Paragraph("Specify the model name that will be used in the gateway.")
                                    )
                                ),
                                EditorRow(
                                    ButtonLink("Use integrated inference", {
                                        onClick() {
                                            displayFormControl({
                                                title: "Choose model",
                                                message: "Choose the model you want to use from the integrated inference.",
                                                formControlItem: ModelPickerControl({ selectedModel: valueFrom("gatewayModelName") }),
                                                dialogSize: "large",
                                                onConfirm(model) {
                                                    find("gatewayModelName").value = model;
                                                    find("gatewayBaseAddress").value = "@integrated";
                                                    find("gatewayApiKey").value = null;
                                                }
                                            })
                                        }
                                    })
                                )
                            ),
                            Heading(
                                "External provider settings",
                                Paragraph("Configure connection details for external inference providers.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayBaseAddress",
                                        value: gateway.value?.parameters.baseAddress,
                                        renderValue(value) {
                                            return Button(value || "No base address");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Base address", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.startsWith("https://") || x == "@integrated", "Base address must be a valid secure URL string"],
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Gateway base address"),
                                        Paragraph("Specify a base URL compatible with the OpenAI interface for your model.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayApiKey",
                                        value: gateway.value?.parameters.apiKey,
                                        renderValue(value) {
                                            return Button("👀")
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "API key", { value }),
                                                onConfirm: x => callback(x.nullWhenEmpty()),
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Gateway API key"),
                                        Paragraph("Specify the API key for gateway communication.")
                                    )
                                ),
                                el.state.listening([isIntegratedModel], () =>
                                    isIntegratedModel.value ?
                                        Editor(
                                            {
                                                id: "gatewayRoutingOption",
                                                value: gateway.value?.parameters.routingOption || "Balanced",
                                                renderValue(value) {
                                                    return Button({
                                                        Balanced: "Balanced",
                                                        Cheapest: "Lowest cost",
                                                        Fastest: "Fastest",
                                                        Quality: "Highest quality",
                                                        Special: "Special"
                                                    }[value] || "Balanced");
                                                },
                                                manipulateValue(value, callback) {
                                                    displayFormControl({
                                                        title: this.editorTitle,
                                                        message: this.editorParagraphs, 
                                                        formControlItem: DataPickerControl({
                                                            data: [
                                                                { text: "Balanced", value: "Balanced" },
                                                                { text: "Lowest cost", value: "Cheapest" },
                                                                { text: "Fastest", value: "Fastest" },
                                                                { text: "Highest quality", value: "Quality" },
                                                                { text: "Special", value: "Special" }
                                                            ],
                                                            value: value || "Balanced",
                                                            allowNone: false
                                                        }),
                                                        onConfirm: callback,
                                                        validate: []
                                                    })
                                                }
                                            },
                                            EditorDescription(
                                                EditorTitle("Routing preference"),
                                                Paragraph("Choose how AIVAX selects a provider for the integrated model."),
                                                Paragraph("Special works like Balanced, but also includes special discounted providers and prioritizes them, regardless of the account's special providers setting.")
                                            )
                                        ) :
                                        Editor(
                                            {
                                                id: "gatewayTransportInterface",
                                                value: gateway.value?.parameters.transportInterface || "OpenAiChatCompletions",
                                                renderValue(value) {
                                                    return Button(value == "OpenAiResponses" ? "OpenAI Responses" : "OpenAI Chat Completions");
                                                },
                                                manipulateValue(value, callback) {
                                                    displayFormControl({
                                                        title: this.editorTitle,
                                                        message: this.editorParagraphs,
                                                        formControlItem: DataPickerControl({
                                                            data: [
                                                                { text: "OpenAI /chat/completions interface", value: "OpenAiChatCompletions" },
                                                                { text: "OpenAI /responses interface", value: "OpenAiResponses" }
                                                            ],
                                                            value: value || "OpenAiChatCompletions",
                                                            allowNone: false
                                                        }),
                                                        onConfirm: callback,
                                                        validate: []
                                                    })
                                                }
                                            },
                                            EditorDescription(
                                                EditorTitle("Communication protocol"),
                                                Paragraph("Choose how the gateway communicates with the provider.")
                                            )
                                        )
                                ),
                                el.state.listening([isIntegratedModel], () =>
                                    isIntegratedModel.value ?
                                        Editor(
                                            {
                                                id: "gatewayAllowedProviders",
                                                value: gateway.value?.parameters.allowedProviders ?? ["*"],
                                                renderValue(value) {
                                                    return Button(value.join(", "));
                                                },
                                                manipulateValue(value, callback) {
                                                    displayFormControl({
                                                        title: this.editorTitle,
                                                        message: this.editorParagraphs,
                                                        formControlItem: ListPickerControl({
                                                            value: [...value],
                                                            placeholder: "Provider tag or *",
                                                            itemName: "provider",
                                                            includePendingValue: true
                                                        }),
                                                        onConfirm: callback,
                                                        validate: [
                                                            [x => x.length > 0, "Add at least one provider tag or *."]
                                                        ]
                                                    })
                                                }
                                            },
                                            EditorDescription(
                                                EditorTitle("Allowed providers"),
                                                Paragraph("Provider tags tried in order, such as azure-us. Use * to allow any provider. Provider tags are shown in each model's provider details.")
                                            )
                                        ) :
                                        el("div")
                                )
                            ),
                            el.state.listening([isComplexityRoutingModel], () =>
                                Content(
                                    isComplexityRoutingModel.value && el.fragment(
                                        Heading("Model routing parameters",
                                            Paragraph("Adjust how the routing model handles and reasons about user requests.")
                                        ),
                                        Section(
                                            Editor(
                                                {
                                                    id: "gatewayModelRouterLowComplexityModelName",
                                                    value: gateway.value?.parameters.modelRoutingParameters?.lowComplexityModelName,
                                                    renderValue(value) {
                                                        return value && ModelBadge(value) || Warning("Not selected");
                                                    },
                                                    manipulateValue(value, callback) {
                                                        displayFormControl({
                                                            title: this.editorTitle,
                                                            message: this.editorParagraphs,
                                                            formControlItem: ModelPickerControl({ selectedModel: value }),
                                                            dialogSize: "large",
                                                            onConfirm: callback,
                                                            validate: []
                                                        })
                                                    }
                                                },
                                                EditorDescription(
                                                    EditorTitle("Low complexity model"),
                                                    Paragraph("Specify which model will be used for low-complexity tasks, such as classification, summarization, and simple conversations.")
                                                )
                                            ),
                                            Editor(
                                                {
                                                    id: "gatewayModelRouterLowComplexityReasoningEffort",
                                                    value: gateway.value?.parameters.modelRoutingParameters?.lowComplexityModelReasoningEffort,
                                                    renderValue(value) {
                                                        return Button(value || MutedText("Unset"));
                                                    },
                                                    manipulateValue(value, callback) {
                                                        displayFormControl({
                                                            title: this.editorTitle,
                                                            message: this.editorParagraphs,
                                                            formControlItem: DataPickerControl({
                                                                data: Constants.NullableReasoningEffortSelectItems,
                                                                value,
                                                                allowNone: false
                                                            }),
                                                            onConfirm: callback,
                                                            validate: []
                                                        })
                                                    }
                                                },
                                                EditorDescription(
                                                    EditorTitle("Low complexity reasoning effort"),
                                                    Paragraph("If supported by the low-complexity model, you can choose their reasoning effort here.")
                                                )
                                            ),
                                        ),
                                        Section(
                                            Editor(
                                                {
                                                    id: "gatewayModelRouterMediumComplexityModelName",
                                                    value: gateway.value?.parameters.modelRoutingParameters?.mediumComplexityModelName,
                                                    renderValue(value) {
                                                        return value && ModelBadge(value) || Warning("Not selected");
                                                    },
                                                    manipulateValue(value, callback) {
                                                        displayFormControl({
                                                            title: this.editorTitle,
                                                            message: this.editorParagraphs,
                                                            formControlItem: ModelPickerControl({ selectedModel: value }),
                                                            dialogSize: "large",
                                                            onConfirm: callback,
                                                            validate: []
                                                        })
                                                    }
                                                },
                                                EditorDescription(
                                                    EditorTitle("Medium complexity model"),
                                                    Paragraph("Specify which model will be used for medium-complexity tasks, such as step-by-step processing, basic programming and deeper analysis.")
                                                )
                                            ),
                                            Editor(
                                                {
                                                    id: "gatewayModelRouterMediumComplexityReasoningEffort",
                                                    value: gateway.value?.parameters.modelRoutingParameters?.mediumComplexityModelReasoningEffort,
                                                    renderValue(value) {
                                                        return Button(value || MutedText("Unset"));
                                                    },
                                                    manipulateValue(value, callback) {
                                                        displayFormControl({
                                                            title: this.editorTitle,
                                                            message: this.editorParagraphs,
                                                            formControlItem: DataPickerControl({
                                                                data: Constants.NullableReasoningEffortSelectItems,
                                                                value,
                                                                allowNone: false
                                                            }),
                                                            onConfirm: callback,
                                                            validate: []
                                                        })
                                                    }
                                                },
                                                EditorDescription(
                                                    EditorTitle("Medium complexity reasoning effort"),
                                                    Paragraph("If supported by the medium-complexity model, you can choose their reasoning effort here.")
                                                )
                                            ),
                                        ),
                                        Section(
                                            Editor(
                                                {
                                                    id: "gatewayModelRouterHighComplexityModelName",
                                                    value: gateway.value?.parameters.modelRoutingParameters?.highComplexityModelName,
                                                    renderValue(value) {
                                                        return value && ModelBadge(value) || Warning("Not selected");
                                                    },
                                                    manipulateValue(value, callback) {
                                                        displayFormControl({
                                                            title: this.editorTitle,
                                                            message: this.editorParagraphs,
                                                            formControlItem: ModelPickerControl({ selectedModel: value }),
                                                            dialogSize: "large",
                                                            onConfirm: callback,
                                                            validate: []
                                                        })
                                                    }
                                                },
                                                EditorDescription(
                                                    EditorTitle("High complexity model"),
                                                    Paragraph("Specify which model will be used for high-complexity tasks, such as full reasoning, advanced programming or highly complex problems.")
                                                )
                                            ),
                                            Editor(
                                                {
                                                    id: "gatewayModelRouterHighComplexityReasoningEffort",
                                                    value: gateway.value?.parameters.modelRoutingParameters?.highComplexityModelReasoningEffort,
                                                    renderValue(value) {
                                                        return Button(value || MutedText("Unset"));
                                                    },
                                                    manipulateValue(value, callback) {
                                                        displayFormControl({
                                                            title: this.editorTitle,
                                                            message: this.editorParagraphs,
                                                            formControlItem: DataPickerControl({
                                                                data: Constants.NullableReasoningEffortSelectItems,
                                                                value,
                                                                allowNone: false
                                                            }),
                                                            onConfirm: callback,
                                                            validate: []
                                                        })
                                                    }
                                                },
                                                EditorDescription(
                                                    EditorTitle("High complexity reasoning effort"),
                                                    Paragraph("If supported by the high-complexity model, you can choose their reasoning effort here.")
                                                )
                                            ),
                                        ),
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "RAG",
                        icon: Icon("book-line"),
                        contents: el.fragment(
                            Heading(
                                "Configure Retrieval-Augmented Generation",
                                "Set up document collections and configure how your gateway retrieves and uses contextual information."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayCollections",
                                        value: gateway.value?.parameters.knowledgeCollections,
                                        renderValue(value) {
                                            return Button(value?.length && value.length + ' collection(s)' || MutedText("None selected"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: CollectionPickerControl({ allowNone: true, multiple: true, value }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("RAG collections"),
                                        Paragraph("Specify one or more collections of documents that will be fed to the gateway."),
                                        Paragraph("You can modify the strategy and parameters for collecting RAG documents below.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayQueryStrategy",
                                        value: gateway.value?.parameters.queryStrategy.at(0) ?? "Plain",
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: [
                                                        {
                                                            value: "Plain",
                                                            text: TextHeading(null, "Plain", "Uses the user's last message as is as a query in RAG.")
                                                        },
                                                        {
                                                            value: "Concatenate",
                                                            text: TextHeading(null, "Concatenate", "Concatenates the last N chat messages as a query in RAG.")
                                                        },
                                                        {
                                                            value: "FullRewrite",
                                                            text: TextHeading(null, "Full rewrite", "Uses a smaller model to rewrite the last N context messages.")
                                                        },
                                                        {
                                                            value: "UserRewrite",
                                                            text: TextHeading(null, "User rewrite", "Uses a smaller model to rewrite only the user's last N messages.")
                                                        },
                                                        {
                                                            value: "QueryFunction",
                                                            text: TextHeading(null, "Query function", "Inserts a specialized search tool into the conversation context.")
                                                        }
                                                    ],
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Query strategy"),
                                        Paragraph("Specify the query strategy that will be used in the conversation to obtain documents."),
                                        Paragraph("Read about the query strategies ", ExternalLink("in our documentation", "https://docs.aivax.net/docs/inference/ai-gateway#recommended-production-configuration"), ".")
                                    )
                                )
                            ),
                            Heading(
                                "Reranking",
                                Paragraph("Choose how retrieved collection documents are reordered before being added to the gateway context.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayRerankerName",
                                        value: getReranker(rerankers, gateway.value?.parameters.rerankerName)?.name ?? defaultReranker.name,
                                        renderValue(value) {
                                            return Button(getReranker(rerankers, value)?.name || value || defaultReranker.name);
                                        },
                                        async manipulateValue(value, callback) {
                                            callback(await PickRerankerModel({
                                                selectedModel: getReranker(rerankers, value)?.name ?? defaultReranker.name
                                            }));
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Collection reranker"),
                                        Paragraph("Controls which reranker is applied after collection search candidates are resolved.")
                                    )
                                )
                            ),
                            Heading(
                                "Strategy parameters",
                                Paragraph("These parameters are used to control how documents are retrieved.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayConcatenateSize",
                                        value: gateway.value?.parameters.queryStrategyParameters?.concatenateContextSize ?? 2,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 100, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Concatenation window"),
                                        Paragraph("Controls how many messages should be concatenated to create the RAG search query."),
                                        Paragraph("This parameter is only used in the ", InlineCode("Concatenate"), " and ", InlineCode("Plain"), " strategies.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayRewriteSize",
                                        value: gateway.value?.parameters.queryStrategyParameters?.rewriteContextSize ?? 2,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 100, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Rewrite window"),
                                        Paragraph("Controls how many messages should be considered in the RAG rewrite."),
                                        Paragraph("This parameter is only used in the ", InlineCode("UserRewrite"), " and ", InlineCode("FullRewrite"), " strategies.")
                                    )
                                )
                            ),
                            Heading(
                                "Retrieval parameters",
                                Paragraph("Adjust the control of how many documents are retrieved in a query.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayRetrievalMaximumCount",
                                        value: gateway.value?.parameters.knowledgeBaseMaximumResults ?? 5,
                                        renderValue(value) {
                                            return Button(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 100, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum results"),
                                        Paragraph("Controls how many documents are retrieved in a query."),
                                        Paragraph("The more documents retrieved, the more tokens will be consumed in the conversation, however, the greater the chance of obtaining relevant documents.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayRetrievalMinimumScore",
                                        value: gateway.value?.parameters.knowledgeBaseMinimumScore ?? 0.4,
                                        renderValue(value) {
                                            return Button(formatFloat(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 1, step: 0.05 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Minimum score"),
                                        Paragraph("Specifies the minimum relevance score for a document to be considered relevant in a query."),
                                        Paragraph("This adjustment depends mainly on the quality of your document collection. Start with a low number, such as 0.3, " +
                                            "and increase it to filter irrelevant documents."
                                        )
                                    )
                                )
                            ),
                            Heading(
                                "Context and meta-information",
                                Paragraph("Configure how retrieved documents are presented in the conversation context.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayKnowledgeUseMetaDescriptions",
                                        value: gateway.value?.parameters.knowledgeUseMetaDescriptions ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "yes", "no");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Use meta descriptions"),
                                        Paragraph("Specifies whether meta descriptions, like collections meaning and context, should be used when presenting retrieved documents in the conversation context."),
                                        Paragraph("Enabling this option can provide more context and improve the relevance of the information presented.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Skills",
                        icon: Icon("lightbulb-ai-line"),
                        contents: el.fragment(
                            Heading(
                                "Setup skills for this gateway",
                                "Skills allow you to create specialized behaviors for your gateway, enabling it to perform specific tasks or respond in particular ways."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewaySkills",
                                        value: gateway.value?.parameters.skills,
                                        renderValue(value) {
                                            return Button(value?.length && value.length + ' skill(s)' || MutedText("None selected"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: SkillPickerControl({ allowNone: true, multiple: true, value }),
                                                onConfirm: callback,
                                                dialogSize: "medium",
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Skills"),
                                        Paragraph("Specify one or more skills that will be associated with the gateway.")
                                    )
                                ),
                            ),
                            Heading(
                                "Tool behavior",
                                "Change the visibility of tools in the conversation context as skills are selected by the model."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayHideToolsWithoutSkills",
                                        value: gateway.value?.parameters.hideToolsWithoutSkill ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "yes", "no");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Hide tools without skills"),
                                        Paragraph("Automatically hides the tools available in the conversation, except for those in the skill selected by the model."),
                                        Paragraph("This allows functions to be focused and deterministic to the skills selected by the model.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayAlwaysVisibleTools",
                                        value: gateway.value?.parameters.alwaysVisibleTools,
                                        renderValue(value) {
                                            return value?.length && BadgeList(value.slice(0, 3).map(r => Badge(r))) || MutedText("None selected");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: ToolNamePicker(null, { value }),
                                                onConfirm: callback,
                                                dialogSize: "medium",
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Always visible tools"),
                                        Paragraph("Specify one or more tools that will always be visible in the context, regardless of the skill selected by the model."),
                                        Paragraph("This option is only relevant when the option to hide tools without skills is enabled.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Instructions",
                        icon: Icon("user-4-line"),
                        contents: el.fragment(
                            Heading(
                                "Define system instructions and behavior",
                                "Control how your AI model responds by setting system instructions, personality, and output formatting rules."
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewaySystemInstructions",
                                        value: gateway.value?.parameters.systemInstruction,
                                        renderValue(value) {
                                            return BigTextPreview(value)
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("System instructions"),
                                        Paragraph("System instructions specify the model's behavior, its personality, what it should and should not do."),
                                    )
                                ),
                                EditorRow(
                                    ButtonLink("Compose System Prompt with AI", {
                                        async onClick() {

                                            const enabledSkills = valueFrom("gatewaySkills") ?? [];
                                            const userSkills = await api.getJson("/api/v1/skills");
                                            const skills = userSkills.data.filter(s => enabledSkills.includes(s.id)).map(s => ({ slug: s.slug, description: s.description }));

                                            requestAiEdit({
                                                inputData: {
                                                    "currentInstructions": find("gatewaySystemInstructions").value,
                                                    "availableSkills": skills,
                                                    "modelTools": {
                                                        "builtinTools": gateway.value?.parameters.sentinelOptions?.enabledFunctions,
                                                        "rawTools": gateway.value?.parameters.tools,
                                                        "mcpSources": gateway.value?.parameters.mcpSources,
                                                        "protocolTools": gateway.value?.parameters.protocolFunctionSources
                                                    }
                                                },
                                                subject: "system instruction",
                                                description: "Write the system instructions with AI."
                                            }).then(result => {
                                                acceptDiff(find("gatewaySystemInstructions").value, result).then((result) => {
                                                    find("gatewaySystemInstructions").value = result;
                                                });
                                            });
                                        }
                                    })
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewaySystemInstructionSources",
                                        value: gateway.value?.parameters.systemInstructionsSources ?? [],
                                        renderValue(value) {
                                            return Button(value?.length + " source(s)");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value, resize: false }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => Array.isArray(x), "The instruction source list must be an array of objects."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Remote system instructions sources"),
                                        Paragraph("Specify remote sources to obtain the system instructions that will be used in the conversation."),
                                    )
                                ),
                            ),
                            Heading(
                                "Prefilling and stopping",
                                Paragraph("Adjust initialization and text generation stop parameters.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayPromptTemplate",
                                        value: gateway.value?.parameters.userPromptTemplate,
                                        renderValue(value) {
                                            return BigTextPreview(value)
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("User prompt template"),
                                        Paragraph("Templating in the user prompt allows you to adjust how the user's input is interpreted by the model."),
                                        Paragraph("Enter the user prompt replacing ", InlineCode("{prompt}"), " with the text that the user enters."),
                                        Warning(
                                            Paragraph(
                                                "Activating this parameter disables the ability to send multi-modal content to the model, such as images, audios, or documents."
                                            )
                                        )
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayAssistantPrefill",
                                        value: gateway.value?.parameters.assistantPrefill,
                                        renderValue(value) {
                                            return BigTextPreview(value)
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Assistant prefill"),
                                        Paragraph("Specify the initialization of the assistant's message. This can be useful for controlling the text output of the model."),
                                        Warning(
                                            Paragraph(
                                                "This parameter is not compatible with models that think (deep thinking). Activating this parameter for models with deep thinking " +
                                                "may result in unexpected responses, errors, or exclusion from their thought process."
                                            ),
                                            Paragraph(
                                                "Activating this parameter disables the model's ability to generate multi-modal responses, such as audios or images."
                                            )
                                        )
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayStopSentence",
                                        value: gateway.value?.parameters.stop,
                                        renderValue(value) {
                                            return Button(value && ellipsis(JSON.stringify(value), 50) || MutedText("Empty"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: CodeEditor({ value }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Stop sentence"),
                                        Paragraph("Specify a stop content for the model. It will stop generating content after the occurrence of this parameter.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayIncludePrefillingParameters",
                                        value: gateway.value?.parameters.includePrefillingInMessages || false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "yes", "no");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Include prefilling in messages"),
                                        Paragraph("By default, the model does not include the initialization and stop content in its response."),
                                        Paragraph("Enable to include the initialization and stop content in the response generated by the model.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Parameters + Tuning",
                        icon: Icon("equalizer-3-line"),
                        contents: el.fragment(
                            Heading(
                                "Sampling parameters",
                                Paragraph("Control randomness and diversity in model responses.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayTemperature",
                                        value: gateway.value?.parameters.temperature,
                                        renderValue(value) {
                                            return Button(value && formatFloat(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 2, step: 0.05 }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Temperature"),
                                        Paragraph("Temperature controls how creative the model is to generate answers."),
                                        Paragraph("Higher temperatures include more tokens in its prediction window, which can increase the diversity of the generated answers."),
                                        Paragraph("Lower temperatures make the model more deterministic.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayTopP",
                                        value: gateway.value?.parameters.topP,
                                        renderValue(value) {
                                            return Button(value && formatFloat(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 1, step: 0.05 }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Top P"),
                                        Paragraph("An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass."),
                                        Paragraph("So 0.1 means only the tokens comprising the top 10% probability mass are considered.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayPresencePenalty",
                                        value: gateway.value?.parameters.presencePenalty,
                                        renderValue(value) {
                                            return Button(value && formatFloat(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: -2, max: 2, step: 0.05 }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Presence penalty"),
                                        Paragraph("Positive values penalize new tokens based on whether they appear in the text so far, increasing the model's likelihood to talk about new topics.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayFrequencyPenalty",
                                        value: gateway.value?.parameters.frequencyPenalty,
                                        renderValue(value) {
                                            return Button(value && formatFloat(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: -2, max: 2, step: 0.05 }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Frequency penalty"),
                                        Paragraph("Positive values penalize new tokens based on their existing frequency in the text so far, decreasing the model's likelihood to repeat the same line verbatim.")
                                    )
                                )
                            ),
                            Heading(
                                "Multi-modal content",
                                Paragraph("Configure how the gateway handles images, audio, and video content.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayMultimodalResolverParameters",
                                        value: (({ multimodalResolverParameters: resolver, enabledMultimodalFeatures: features = [] }) => {
                                            const hasFeature = feature => features.includes(feature) || features.includes("All");
                                            return resolver ? {
                                                imageEngine: resolver.imageEngine?.at(0) ?? null,
                                                audioEngine: resolver.audioEngine?.at(0) ?? null,
                                                videoEngine: resolver.videoEngine?.at(0) ?? null,
                                                fileEngine: resolver.fileEngine?.at(0) ?? null
                                            } : {
                                                imageEngine: hasFeature("Image") ? "InferenceLow" : null,
                                                audioEngine: hasFeature("Audio") ? "InferenceLow" : null,
                                                videoEngine: hasFeature("Video") ? "InferenceLow" : null,
                                                fileEngine: hasFeature("File") ? "InferenceLow" : hasFeature("OtherFiles") ? "Ocr" : null
                                            };
                                        })(gateway.value?.parameters ?? {}),
                                        renderValue(value) {
                                            const { ImageEngineValues, AudioEngineValues, VideoEngineValues, FileEngineValues } = Constants.MultimodalResolverEngines;
                                            const enabled = [
                                                ["Image", ImageEngineValues[value?.imageEngine]],
                                                ["Audio", AudioEngineValues[value?.audioEngine]],
                                                ["Video", VideoEngineValues[value?.videoEngine]],
                                                ["Files", FileEngineValues[value?.fileEngine]]
                                            ].filter(([, engine]) => engine);

                                            return Button(
                                                enabled.length > 0
                                                    && enabled.map(([type, engine]) => `${type}: ${engine}`).join(", ")
                                                    || MutedText("None")
                                            );
                                        },
                                        manipulateValue(value, callback) {
                                            const { ImageEngineValues, AudioEngineValues, VideoEngineValues, FileEngineValues } = Constants.MultimodalResolverEngines;

                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: SpecializedControl({
                                                    value,
                                                    fields: [
                                                        ["imageEngine", "Images", ImageEngineValues],
                                                        ["audioEngine", "Audio", AudioEngineValues],
                                                        ["videoEngine", "Video", VideoEngineValues],
                                                        ["fileEngine", "Files", FileEngineValues]
                                                    ].map(([name, label, engineValues]) => ({
                                                        name,
                                                        label,
                                                        control: Select({
                                                            value: value?.[name] ?? "",
                                                            items: [
                                                                { value: "", text: "Disabled" },
                                                                ...Object.entries(engineValues).map(([engine, text]) => ({ value: engine, text }))
                                                            ]
                                                        })
                                                    }))
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Pre-process multi-modal resources"),
                                        Paragraph("Choose how images, audio, video, and files in the conversation are converted to text before reaching the model."),
                                        Information(
                                            Paragraph(
                                                "Inference (low effort) uses a smaller multimodal model and Inference (high effort) a larger, more accurate one.",
                                                "OCR extracts the text of images and files. Speech-to-text transcribes speech only."
                                            ),
                                            Paragraph(
                                                "With an inference engine, PDFs are described by the model and other files use OCR.",
                                                "Each content is processed only once per engine and cached for a month until it is no longer used."
                                            )
                                        )
                                    )
                                ),
                            ),
                            Heading(
                                "Token limits and output control",
                                Paragraph("Set constraints on response length and model verbosity.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayMaxCompletionTokens",
                                        value: gateway.value?.parameters.maxCompletionTokens,
                                        renderValue(value) {
                                            return Button(value && formatNumber(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 524288, step: 1024 }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum completion tokens"),
                                        Paragraph("An upper bound for the number of tokens that can be generated for a completion, including visible output tokens and reasoning tokens.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayVerbosity",
                                        value: gateway.value?.parameters.verbosity,
                                        renderValue(value) {
                                            return Button(value || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: [
                                                        {
                                                            value: null, text: TextHeading(null,
                                                                "None",
                                                                "Let the model decide."
                                                            )
                                                        },
                                                        {
                                                            value: "low", text: TextHeading(null,
                                                                "Low",
                                                                "Terse, minimal prose."
                                                            )
                                                        },
                                                        {
                                                            value: "medium", text: TextHeading(null,
                                                                "Medium",
                                                                "Balanced detail."
                                                            )
                                                        },
                                                        {
                                                            value: "high", text: TextHeading(null,
                                                                "High",
                                                                "Highly detailed responses, great for audits, teaching or heand-offs."
                                                            )
                                                        },
                                                    ],
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Verbosity"),
                                        Paragraph("The verbosity parameter lets you hint the model to be more or less expansive in its replies."),
                                        Warning(
                                            Paragraph("Not all models support this parameter.")
                                        )
                                    )
                                ),
                            ),
                            Heading(
                                "Context window management",
                                Paragraph("Control how the conversation context is managed and maintained.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayContextMaximumSize",
                                        value: gateway.value?.parameters.contextMaximumSize,
                                        renderValue(value) {
                                            return Button(value && formatNumber(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 5_000_000, step: 8192 }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum context length"),
                                        Paragraph(
                                            "Define the maximum input context window size in the number of tokens."
                                        ),
                                        Paragraph(
                                            "An approximate calculation is used to estimate how many tokens are in use in the current conversation, which may ",
                                            "not be exact for all models."
                                        )
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayContextOverflowAction",
                                        value: gateway.value?.parameters.contextOverflowAction.at(0) || "Throw",
                                        renderValue(value) {
                                            return Button(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: [
                                                        {
                                                            value: "Throw", text: TextHeading(null,
                                                                "Throw an error",
                                                                "Throws an exception that prevents inference from continuing."
                                                            )
                                                        },
                                                        {
                                                            value: "Truncate", text: TextHeading(null,
                                                                "Truncate",
                                                                "Truncates the oldest messages from the conversation until it fits the context limit."
                                                            )
                                                        },
                                                    ],
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Context overflow action"),
                                        Paragraph("Defines the action to be taken when the conversation reaches the maximum context size.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayToolContextSize",
                                        value: gateway.value?.parameters.toolContextCount,
                                        renderValue(value) {
                                            return Button(value && formatNumber(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 500, step: 1 }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Tool message count"),
                                        Paragraph(
                                            "Define how many tool result messages should be preserved in the conversation context window.",
                                            "The last N tool messages will be preserved in the conversation context. The others will have the result ",
                                            "cut from the context to save tokens."
                                        ),
                                        Paragraph(
                                            "Not specifying this parameter disables this feature and preserves all tool result messages."
                                        )
                                    )
                                ),
                            ),
                            Heading(
                                "Reasoning models configuration",
                                Paragraph("Adjust settings specific to reasoning-capable models.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayReasoningEffort",
                                        value: gateway.value?.parameters.reasoningEffort,
                                        renderValue(value) {
                                            return Button(value || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: Constants.NullableReasoningEffortSelectItems,
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Reasoning effort"),
                                        Paragraph("Constrains effort on reasoning for reasoning models."),
                                        Paragraph("Reducing reasoning effort can result in faster responses and fewer tokens used on reasoning in a response.")
                                    )
                                ),
                            ),
                            Heading(
                                "Date and time",
                                Paragraph("Define the time zone in which the model reads and writes dates and times.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayTimeZone",
                                        value: gateway.value?.parameters.timeZone ?? "America/Sao_Paulo",
                                        renderValue(value) {
                                            return Button(value ?? "America/Sao_Paulo");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: Select({
                                                    value: value ?? "America/Sao_Paulo",
                                                    items: Constants.TimeZoneSelectItems,
                                                    itemsRaw: value && !Constants.TimeZoneSelectItems.some(group =>
                                                        group.items.some(item => item.value === value)
                                                    ) ? el("optgroup", { label: "Current time zone" },
                                                        el("option", { value, selected: true }, value)
                                                    ) : null
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Time zone"),
                                        Paragraph("IANA time zone used by the get_date_time tool, scheduled messages, recurring jobs and the Bash tool. Daylight-saving changes are applied automatically.")
                                    )
                                )
                            ),
                            Heading("Flags and normalization switches",
                                Paragraph("Configure specific model mechanisms and switches that can affect the way the model operates in significant ways.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayFlags",
                                        value: gateway.value?.parameters.flags ?? [],
                                        renderValue(value) {
                                            return Button(value?.length + " flags");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: MultiDataPickerControl({
                                                    data: [
                                                        {
                                                            value: "DisableMultiModalInput",
                                                            text: TextHeading(null,
                                                                "No multi-modal input",
                                                                Paragraph("Do not send multi-modal content to the model, even if the model allows it.")
                                                            )
                                                        },
                                                        {
                                                            value: "ThinkingProcess",
                                                            text: TextHeading(null,
                                                                "Implicit thinking",
                                                                Paragraph("Specifies that the model uses a thinking/reasoning process, but it's reasoning tokens are invisible.")
                                                            )
                                                        },
                                                        {
                                                            value: "DiffusingStream",
                                                            text: TextHeading(null,
                                                                "Diffusing stream",
                                                                Paragraph("Specifies that the model is an diffusion model.")
                                                            )
                                                        },
                                                        {
                                                            value: "PreserveThinkingTokens",
                                                            text: TextHeading(null,
                                                                "Preserve thinking tokens",
                                                                Paragraph("Preserve assistant thinking blocks (\"<think>\") during inference.")
                                                            )
                                                        },
                                                        {
                                                            value: "RewriteToolCallsAsUserMessages",
                                                            text: TextHeading(null,
                                                                "Rewrite tool calls as user messages",
                                                                Paragraph("Use a compatible, legacy syntax for tool calls and tool responses.")
                                                            )
                                                        },
                                                        {
                                                            value: "StrictUserAssistantTurns",
                                                            text: TextHeading(null,
                                                                "Strict user-assistant turns",
                                                                Paragraph("Enforce strict alternation between user and assistant messages. This disables tool messages.")
                                                            )
                                                        },
                                                        {
                                                            value: "DisableReasoningSummary",
                                                            text: TextHeading(null,
                                                                "Disable reasoning summary",
                                                                Paragraph("Disables the reasoning summary feature.")
                                                            )
                                                        }
                                                    ],
                                                    value
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Flags"),
                                        Paragraph("Configures advanced model mechanisms and switches.")
                                    )
                                ),
                            )
                        )
                    },
                    {
                        title: "Tools + MCP",
                        icon: Icon("box-1-line"),
                        contents: el.fragment(
                            Heading(
                                "Server-side tools",
                                Paragraph("Define external functions that can be used by agentic models to complement the answer in real time, such as MCP tools."),
                                Paragraph("For more information, see ", ExternalLink("the documentation", "https://docs.aivax.net/docs/tools/mcp"), ".")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayMcpSources",
                                        value: gateway.value?.parameters.mcpSources ?? [],
                                        renderValue(value) {
                                            return Button(value?.length + " source(s)");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value, resize: false }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => Array.isArray(x), "The MCP source list must be an array of objects."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        Image("/assets/mcp.svg", {
                                            style: {
                                                height: "22px",
                                                marginBottom: "1em"
                                            },
                                            classList: "light-logo"
                                        }),
                                        EditorTitle("MCP sources"),
                                        Paragraph("Specifies a list of MCP (Model Context Protocol) sources to define functions in the agent."),
                                        Paragraph(
                                            "Read more about MCP sources in the ",
                                            ExternalLink("documentation", "https://docs.aivax.net/docs/tools/mcp"),
                                            ".",
                                        )
                                    )
                                )
                            ),
                            Heading(
                                "Built-in tools options",
                                Paragraph(
                                    "Specify specific settings for the built-in tools."
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewaySentinelEnabledFunctions",
                                        value: gateway.value?.parameters.sentinelOptions?.enabledFunctions ?? [],
                                        renderValue(value) {
                                            return Button(value?.length && value.length + " function(s)" || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: MultiDataPickerControl({
                                                    value,
                                                    type: 'grid',
                                                    data: [
                                                        {
                                                            separator: "Web related tools"
                                                        },
                                                        {
                                                            value: "OpenUrl",
                                                            text: TextHeading(
                                                                Icon("link"), "Open URLs",
                                                                Paragraph("Allows the model to access the content of external links.")
                                                            )
                                                        },
                                                        {
                                                            value: "WebSearch",
                                                            text: TextHeading(
                                                                Icon("search-line"), "Web search",
                                                                Paragraph("Allows the model to search the web.")
                                                            )
                                                        },
                                                        {
                                                            value: "XPostsSearch",
                                                            text: TextHeading(
                                                                Icon("twitter-x-line"), "X Tools",
                                                                Paragraph("Allows the model to search for relevant posts on X (aka. Twitter).")
                                                            )
                                                        },
                                                        {
                                                            value: "AdvancedWebUsage",
                                                            text: TextHeading(
                                                                Icon("drag-drop-line"), "Advanced web search",
                                                                Paragraph("Allows the model to use a fully-featured browser to navigate, interact and search the web.")
                                                            )
                                                        },
                                                        {
                                                            separator: "Code execution"
                                                        },
                                                        {
                                                            value: "Code",
                                                            text: TextHeading(
                                                                Icon("braces-line"), "Code execution",
                                                                Paragraph("Allows the model to execute JavaScript code.")
                                                            )
                                                        },
                                                        {
                                                            value: "Request",
                                                            text: TextHeading(
                                                                Icon("mail-send-line"), "Advanced Request",
                                                                Paragraph("Allows the model to make an HTTP request to an external resource, specifying method, headers, and body.")
                                                            )
                                                        },
                                                        {
                                                            separator: "Media generation"
                                                        },
                                                        {
                                                            value: "ImageGeneration",
                                                            text: TextHeading(
                                                                Icon("image-ai-line"), "Image Generation",
                                                                Paragraph("Allows the model to generate images.")
                                                            )
                                                        },
                                                        {
                                                            separator: "Data persistency tools"
                                                        },
                                                        {
                                                            value: "Remember",
                                                            text: TextHeading(
                                                                Icon("brain-2-line"), "Memory",
                                                                Paragraph("Allows the model to remember information for future use.")
                                                            )
                                                        },
                                                        {
                                                            value: "DateTime",
                                                            text: TextHeading(
                                                                Icon("time-line"), "Current date and time",
                                                                Paragraph("Returns the current date, time and day of the week in the configured time zone.")
                                                            )
                                                        },
                                                        {
                                                            separator: "Document generation"
                                                        },
                                                        {
                                                            value: "GenerateDocument",
                                                            text: TextHeading(
                                                                Icon("file-paper-2-line"), "Generate document",
                                                                Paragraph("Allows the model to create PDF files.")
                                                            )
                                                        },
                                                        {
                                                            value: "GenerateWebPage",
                                                            text: TextHeading(
                                                                Icon("code-s-slash-line"), "Generate web page",
                                                                Paragraph("Allows the model to create and host HTML pages.")
                                                            )
                                                        }
                                                    ]
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Enabled built-in tools"),
                                        Paragraph("Enable or disable the built-in agentic tools.")
                                    )
                                )
                            ),
                            Heading(
                                "Bash",
                                Paragraph("Bash virtual environment settings for the gateway.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayEnableBash",
                                        value: gateway.value?.parameters.enableBash || false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Enable virtual bash"),
                                        Paragraph("Enables the virtual Bash environment, which encapsulates all model tools in an isolated Unix environment."),
                                        Paragraph("When this option is enabled, the model's available tools become CLI tools, along with basic file manipulation and command execution functions."),
                                        Warning("Warning: this option is experimental and may cause unexpected behavior in some models. Use with caution."),
                                        Warning("Warning: client-side tools are not supported in this system mode. Only protocol functions, built-in or MCP are supported.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayIncludeWorkspaceInstructions",
                                        value: gateway.value?.parameters.includeWorkspaceInstructions ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Include workspace instructions"),
                                        Paragraph("Automatically includes AGENTS.md and MEMORY.md files found at the root of the Bash workspace as system instructions."),
                                        Paragraph("Each file is limited to 16K characters before it is added to the model context.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayAllowBashDataPersistency",
                                        value: gateway.value?.parameters.bashOptions?.allowDataPersistence || false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Allow bash data persistence"),
                                        Paragraph("Allows data generated in the virtual Bash environment to persist between sessions."),
                                        Paragraph("When this option is enabled, data generated in the virtual Bash environment will be saved and can be reused in future sessions."),
                                        Warning("Warning: enabling this option will use extra account storage to save the virtual Bash environment data, which may incur additional costs.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayBashToolExclusionMode",
                                        value: gateway.value?.parameters.bashOptions?.toolExclusionMode?.at(0) || "BlackList",
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: [
                                                        {
                                                            value: "BlackList",
                                                            text: TextHeading(null, "BlackList", "All tools are available except those listed.")
                                                        },
                                                        {
                                                            value: "WhiteList",
                                                            text: TextHeading(null, "WhiteList", "Only the listed tools are available.")
                                                        }
                                                    ],
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Tool exclusion mode"),
                                        Paragraph("Defines whether the tool list below acts as a blacklist or whitelist in the virtual Bash environment.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayBashToolList",
                                        value: gateway.value?.parameters.bashOptions?.toolList ?? [],
                                        renderValue(value) {
                                            return value?.length && BadgeList(value.slice(0, 3).map(r => Badge(r))) || MutedText("None selected");

                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: ToolNamePicker(null, { value }),
                                                onConfirm: callback,
                                                dialogSize: "medium",
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Bash tool list"),
                                        Paragraph("Specify the tools that will be included or excluded from the virtual Bash environment, according to the exclusion mode above."),
                                        Paragraph("Names are case-insensitive. Use * to match any number of characters (for example, something_*) or ? to match one character. Press Enter to add each name or pattern.")
                                    )
                                ),
                            ),
                            Heading(
                                "Web search configuration",
                                Paragraph("Configure how the built-in web search tool operates.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayBuiltinWebSearchMode",
                                        value: gateway.value?.parameters.builtinFunctionsOptions?.webSearchMode.at(0) || "Summarized",
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: [
                                                        {
                                                            value: "Summarized", text: TextHeading(
                                                                null,
                                                                "Summarized",
                                                                "More economical. Uses a smaller model to summarize the search results."
                                                            )
                                                        },
                                                        {
                                                            value: "Full", text: TextHeading(
                                                                null,
                                                                "Full",
                                                                "More accurate. Embeds the content of the search results in the conversation."
                                                            )
                                                        },
                                                    ],
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Web search mode"),
                                        Paragraph("Specifies the mode of operation of the internet search function and how it should obtain results.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayBuiltinWebSearchMaxResults",
                                        value: gateway.value?.parameters.builtinFunctionsOptions?.webSearchMaxResults || 10,
                                        renderValue(value) {
                                            return Button(value && formatNumber(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 15, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Web search max results"),
                                        Paragraph("Defines how many results the internet search should bring."),
                                        Paragraph("The more results, the greater the chance of the search bringing relevant content, however, the greater the consumption of tokens."),
                                        Information(
                                            "This parameter is only valid when the search mode is ", InlineCode("Full")
                                        )
                                    )
                                ),
                            ),
                            Heading(
                                "Memory Configuration",
                                Paragraph("Adjust memory storage settings for integrated tools.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayBuiltinAllowSharedMemory",
                                        value: gateway.value?.parameters.builtinFunctionsOptions?.allowSharedMemory ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "public", "private");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Memory visibility"),
                                        Paragraph("Defines whether this gateway can consume memories from other gateways or only its own memories.")
                                    )
                                ),
                            ),
                            Heading(
                                "Image generation configuration",
                                Paragraph("Configure the built-in image generation tool settings.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayBuiltinImageGenModelName",
                                        value: gateway.value?.parameters.builtinFunctionsOptions?.imageGenerationModelName,
                                        renderValue(value) {
                                            return Button(value || MutedText("Default"));
                                        },
                                        async manipulateValue(value, callback) {
                                            callback(await PickImageGenModel({ selectedModel: value }));
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Image generation model"),
                                        Paragraph("Specifies which model will be used for image inference. Some models support image editing, others do not.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayBuiltinImageGenQuality",
                                        value: gateway.value?.parameters.builtinFunctionsOptions?.imageGenerationQuality.at(0) || "Medium",
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: [
                                                        {
                                                            value: "Low", text: TextHeading(
                                                                null,
                                                                "Low",
                                                                "Most economical and fastest option at the cost of images with many artifacts and defects."
                                                            )
                                                        },
                                                        {
                                                            value: "Medium", text: TextHeading(
                                                                null,
                                                                "Medium",
                                                                "Balance between cost and quality. May generate eventual artifacts."
                                                            )
                                                        },
                                                        {
                                                            value: "High", text: TextHeading(
                                                                null,
                                                                "High",
                                                                "Generates high-quality images at a slightly higher cost."
                                                            )
                                                        },
                                                        {
                                                            value: "Highest", text: TextHeading(
                                                                null,
                                                                "Highest",
                                                                "The best option for realistic and high-definition images."
                                                            )
                                                        },
                                                    ],
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Image generation quality"),
                                        Paragraph("Defines the quality of image generation."),
                                        Paragraph("Better qualities tend to cost more and take longer to generate images.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayBuiltinImageGenMaxImages",
                                        value: gateway.value?.parameters.builtinFunctionsOptions?.imageGenerationMaxResults || 2,
                                        renderValue(value) {
                                            return Button(value && formatNumber(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 4, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Image generation image count"),
                                        Paragraph("Define how many images should be generated in every image generation call.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayBuiltinImageGenAllowMatureContent",
                                        value: gateway.value?.parameters.builtinFunctionsOptions?.imageGenerationAllowMatureContent || false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "allow", "disallow");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Image generation mature content"),
                                        Paragraph("Allows or not the generation of explicit and/or adult content in image generation."),
                                        Paragraph("By activating this option, you agree with the ", ExternalLink("clause 3.2", "https://docs.aivax.net/docs/legal/terms-of-service#32-adult-explicit-and-sensitive-content"),
                                            " about generation of explicit and adult material.")
                                    )
                                ),
                            ),
                            Heading(
                                "Tool parsing",
                                Paragraph(
                                    "Each model and provider has its own tool interpreter. You can use their native one or AIVAX's predefined ones."
                                ),
                                Paragraph(
                                    "Some algorithms have additional costs for using alternative interpreters for tool calls, such as the ReAct Framework which uses an auxiliary model."
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayToolHandler",
                                        value: gateway.value?.parameters.knownToolHandlerName,
                                        renderValue(value) {
                                            return Button(value || MutedText("Native"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: [
                                                        {
                                                            value: null, text: TextHeading(
                                                                null,
                                                                "native",
                                                                Paragraph(
                                                                    "Uses the model's native tool interpreter."
                                                                )
                                                            )
                                                        },
                                                        { separator: "react.v1" },
                                                        // {
                                                        //     value: "react.v1:gemini-2.0-flash-lite", text: TextHeading(
                                                        //         null,
                                                        //         "react.v1:gemini-2.0-flash-lite",
                                                        //         Paragraph(
                                                        //             "ReAct interpreter based on Gemini 2.5 Flash Lite."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.025/cached"),
                                                        //             TagBadge("$ 0.1/in"),
                                                        //             TagBadge("$ 0.4/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                        // {
                                                        //     value: "react.v1:kimi-k2", text: TextHeading(
                                                        //         null,
                                                        //         "react.v1:kimi-k2",
                                                        //         Paragraph(
                                                        //             "ReAct interpreter based on Kimi-K2."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.05/cached"),
                                                        //             TagBadge("$ 0.10/in"),
                                                        //             TagBadge("$ 3.00/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                        {
                                                            value: "react.v1.selfcall", text: TextHeading(
                                                                null,
                                                                "react.v1.selfcall",
                                                                Paragraph(
                                                                    "ReAct interpreter based on the same model."
                                                                ),
                                                                BadgeList()
                                                            )
                                                        },
                                                        // { separator: "ntvcall" },
                                                        // {
                                                        //     value: "ntvcall.v1:gpt-oss-120b", text: TextHeading(
                                                        //         null,
                                                        //         "ntvcall.v1:gpt-oss-120b",
                                                        //         Paragraph(
                                                        //             "AltNative interpreter based on GPT-OSS-120b."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.1/in"),
                                                        //             TagBadge("$ 0.5/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                        // {
                                                        //     value: "ntvcall.v1:gpt-4o-mini", text: TextHeading(
                                                        //         null,
                                                        //         "ntvcall.v1:gpt-4o-mini",
                                                        //         Paragraph(
                                                        //             "AltNative interpreter based on GPT-4o-mini."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.075/cached"),
                                                        //             TagBadge("$ 0.15/in"),
                                                        //             TagBadge("$ 0.6/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                        // {
                                                        //     value: "ntvcall.v1:gpt-5-mini-low", text: TextHeading(
                                                        //         null,
                                                        //         "ntvcall.v1:gpt-5-mini-low",
                                                        //         Paragraph(
                                                        //             "AltNative interpreter based on GPT-5-mini (low)."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.025/cached"),
                                                        //             TagBadge("$ 0.25/in"),
                                                        //             TagBadge("$ 2.0/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                        // {
                                                        //     value: "ntvcall.v1:gpt-5", text: TextHeading(
                                                        //         null,
                                                        //         "ntvcall.v1:gpt-5",
                                                        //         Paragraph(
                                                        //             "AltNative interpreter based on GPT-5 (dynamic)."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.125/cached"),
                                                        //             TagBadge("$ 1.25/in"),
                                                        //             TagBadge("$ 10/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                        // {
                                                        //     value: "ntvcall.v1:gpt-5.1", text: TextHeading(
                                                        //         null,
                                                        //         "ntvcall.v1:gpt-5.1",
                                                        //         Paragraph(
                                                        //             "AltNative interpreter based on GPT-5.1 (dynamic)."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.125/cached"),
                                                        //             TagBadge("$ 1.25/in"),
                                                        //             TagBadge("$ 10/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                        // {
                                                        //     value: "ntvcall.v1:grok-4-fast", text: TextHeading(
                                                        //         null,
                                                        //         "ntvcall.v1:grok-4-fast",
                                                        //         Paragraph(
                                                        //             "AltNative interpreter based on Grok 4 Fast (non reasoning)."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.05/cached"),
                                                        //             TagBadge("$ 0.20/in"),
                                                        //             TagBadge("$ 0.50/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                        // {
                                                        //     value: "ntvcall.v1:grok-4.1-fast-reasoning", text: TextHeading(
                                                        //         null,
                                                        //         "ntvcall.v1:grok-4.1-fast-reasoning",
                                                        //         Paragraph(
                                                        //             "AltNative interpreter based on Grok 4.1 Fast (reasoning)."
                                                        //         ),
                                                        //         BadgeList(
                                                        //             TagBadge("$ 0.05/cached"),
                                                        //             TagBadge("$ 0.20/in"),
                                                        //             TagBadge("$ 0.50/out")
                                                        //         )
                                                        //     )
                                                        // },
                                                    ],
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Tool calling handler"),
                                        Paragraph("Choose the tool calling handler for this gateway.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayToolExplanations",
                                        value: gateway.value?.parameters.toolInvocationExplanations,
                                        renderValue(value) {
                                            return CheckWrapper(value, "yes", "no")
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Tool explanation parameters"),
                                        Paragraph("Proxy function arguments to include meta-explanatory parameters.")
                                    )
                                ),
                            ),
                            Heading(
                                "Raw tools",
                                Paragraph(
                                    "Specify below the list of native tools that will be used by the model. This JSON object is forwarded to the inference gateway " +
                                    "directly and is not handled by AIVAX."),
                                Paragraph("When using these tools, it will not be possible to use the Sentinel protocol tools.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayTools",
                                        value: gateway.value?.parameters.tools,
                                        renderValue(value) {
                                            return BigTextPreview(JSON.stringify(value, null, 2), "json")
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value, resize: false }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => Array.isArray(x), "The tools list must be an array of objects."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Model tools"),
                                        Paragraph("Specifies a list of tools that will be used by the model.")
                                    )
                                ),
                            ),
                            Heading(
                                "Protocol Functions",
                                Paragraph(
                                    "Specify below the list of protocol functions that will be used by the model."
                                )
                            ),
                            Editor(
                                {
                                    id: "gatewayProtocolFunctionSources",
                                    value: gateway.value?.parameters.protocolFunctionSources ?? [],
                                    deprecated: true,
                                    renderValue(value) {
                                        return Button(value?.length + " source(s)");
                                    },
                                    manipulateValue(value, callback) {
                                        displayFormControl({
                                            title: this.editorTitle,
                                            message: this.editorParagraphs,
                                            formControlItem: ListPickerControl({ value, validateItem: x => x.startsWith("https://") }),
                                            onConfirm: callback,
                                            validate: []
                                        })
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("External tool sources "),
                                    Paragraph("Specifies a list of secure URLs that are used to retrieve tools in the conversation."),
                                    Information(
                                        Paragraph(
                                            "It is recommended to implement authentication to bring the functions according to specific contexts and gateways.",
                                            "For more information, see ",
                                            ExternalLink("the documentation", "https://docs.aivax.net/docs/tools/protocol-functions"),
                                            "."
                                        )
                                    )
                                )
                            ),
                            Editor(
                                {
                                    id: "gatewayProtocolFunctions",
                                    value: gateway.value?.parameters.protocolFunctions,
                                    deprecated: true,
                                    renderValue(value) {
                                        return BigTextPreview(JSON.stringify(value, null, 2), "json")
                                    },
                                    manipulateValue(value, callback) {
                                        displayFormControl({
                                            title: this.editorTitle,
                                            message: this.editorParagraphs,
                                            dialogSize: "large",
                                            formControlItem: JsonControl({ value, resize: false }),
                                            onConfirm: callback,
                                            validate: [
                                                [x => Array.isArray(x), "The external tool list must be an array of objects."]
                                            ]
                                        })
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("External tools definitions "),
                                    Paragraph("Specifies a list of static external tools used by the conversation.")
                                )
                            ),
                            Heading(
                                "Advanced settings",
                                Paragraph(
                                    "Specify below settings in JSON format not specified previously.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayAdditionalSettings",
                                        value: gateway.value?.parameters.additionalSettings,
                                        renderValue(value) {
                                            return BigTextPreview(JSON.stringify(value, null, 2), "json")
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value, resize: false }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => typeof x === "object", "The tools list must be an object."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Advanced settings"),
                                        Paragraph("Specify additional advanced settings in JSON format.")
                                    )
                                ),
                            ),
                        )
                    },
                    {
                        title: "Moderation",
                        icon: Icon("shield-line"),
                        contents: el.fragment(
                            Heading(
                                "Content Moderation",
                                Paragraph("Control moderation categories for this AI gateway."),
                                Paragraph("An auxiliary model reads the full textual conversation and classifies the latest user request before sending it to the main model."),
                                Information(
                                    Paragraph("Levels use a 0–10 sensitivity scale: 0 disables the category, 1 blocks only safeguard score 10, and 10 blocks scores 1–10."),
                                    Paragraph("Moderation currently applies only to input text. Generated output and the contents of images, audio, video, and file attachments are not analyzed.")
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "modMode",
                                        value: gateway.value?.parameters.moderationParameters?.mode || "LayeredGuardrail",
                                        renderValue(value) {
                                            return Button(value === "SelfAssessment" ? "Self-assessment" : "Layered guardrail");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    data: [
                                                        {
                                                            value: "LayeredGuardrail", text: TextHeading(null,
                                                                "Layered guardrail",
                                                                "An independent moderation classifier analyzes the input text before the main model and blocks flagged conversations."
                                                            )
                                                        },
                                                        {
                                                            value: "SelfAssessment", text: TextHeading(null,
                                                                "Self-assessment",
                                                                "The main model applies the moderation rules itself and refuses flagged requests through a tool call. Requires a model with tool calling."
                                                            )
                                                        },
                                                    ],
                                                    value,
                                                    allowNone: false
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Moderation mode"),
                                        Paragraph("Defines how the moderation levels are enforced."),
                                        Paragraph(MutedText("Self-assessment has lower cost and latency, but relies on the main model's own judgment."))
                                    )
                                ),
                                Editor(
                                    {
                                        id: "modAdditionalRules",
                                        value: gateway.value?.parameters.moderationParameters?.additionalRules,
                                        renderValue(value) {
                                            return BigTextPreview(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value }),
                                                onConfirm: value => callback(value?.nullWhenEmpty()),
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Additional moderation rules"),
                                        Paragraph("Define gateway-specific policy that the safeguard must consider together with the built-in categories when assigning scores."),
                                        Paragraph(MutedText("Rules guide classification; the configured category levels still determine whether the input is blocked."))
                                    )
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        renderValue() {
                                            return PrimaryButton("Update all");
                                        },
                                        manipulateValue() {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ min: 0, max: 10, step: 1 }),
                                                onConfirm(value) {
                                                    find('modViolenceThreshold').value = value;
                                                    find('modSexualExplicitThreshold').value = value;
                                                    find('modPoliticalThreshold').value = value;
                                                    find('modDangerousContentThreshold').value = value;
                                                    find('modJailbreakThreshold').value = value;
                                                },
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Set global value"),
                                        Paragraph("Click the button next to it to set a global moderation value that will be applied to all categories below, unless individually overridden.")
                                    )
                                ),
                            ),
                            Heading(
                                "Moderation categories",
                                Paragraph("Fine-tune moderation thresholds for each content category.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "modViolenceThreshold",
                                        value: gateway.value?.parameters.moderationParameters?.violenceThreshold,
                                        renderValue(value) {
                                            return Meter({ value, colorShade: true, max: 10 });
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 10, step: 1 }),
                                                onConfirm: callback,
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Moderation: violence and hate speech"),
                                        Paragraph("Specifies the moderation sensitivity for violent, prejudiced, and potentially extremist topics."),
                                        Paragraph(MutedText("Higher values are more restrictive."))
                                    )
                                ),
                                Editor(
                                    {
                                        id: "modSexualExplicitThreshold",
                                        value: gateway.value?.parameters.moderationParameters?.sexualExplicitThreshold,
                                        renderValue(value) {
                                            return Meter({ value, colorShade: true, max: 10 });
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 10, step: 1 }),
                                                onConfirm: callback,
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Moderation: sexual and explicit content"),
                                        Paragraph("Specifies the moderation sensitivity for sexual, explicit, and adult topics."),
                                        Paragraph(MutedText("Higher values are more restrictive."))
                                    )
                                ),
                                Editor(
                                    {
                                        id: "modPoliticalThreshold",
                                        value: gateway.value?.parameters.moderationParameters?.politicalThreshold,
                                        renderValue(value) {
                                            return Meter({ value, colorShade: true, max: 10 });
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 10, step: 1 }),
                                                onConfirm: callback,
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Moderation: political topics"),
                                        Paragraph("Specifies the moderation sensitivity for political topics."),
                                        Paragraph(MutedText("Higher values are more restrictive."))
                                    )
                                ),
                                Editor(
                                    {
                                        id: "modDangerousContentThreshold",
                                        value: gateway.value?.parameters.moderationParameters?.dangerousContentThreshold,
                                        renderValue(value) {
                                            return Meter({ value, colorShade: true, max: 10 });
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 10, step: 1 }),
                                                onConfirm: callback,
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Moderation: dangerous content"),
                                        Paragraph("Specifies the moderation sensitivity for dangerous content, e.g., bombs, weapons, and hacking."),
                                        Paragraph(MutedText("Higher values are more restrictive."))
                                    )
                                ),
                                Editor(
                                    {
                                        id: "modJailbreakThreshold",
                                        value: gateway.value?.parameters.moderationParameters?.jailbreakThreshold,
                                        renderValue(value) {
                                            return Meter({ value, colorShade: true, max: 10 });
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 10, step: 1 }),
                                                onConfirm: callback,
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Moderation: jailbreak attempt"),
                                        Paragraph("Specifies the moderation sensitivity for jailbreak attempts, system instruction reading, or prompt injection."),
                                        Paragraph(MutedText("Higher values are more restrictive."))
                                    )
                                ),
                            )
                        )
                    },
                    {
                        title: "Workers",
                        icon: Icon("settings-3-line"),
                        contents: el.fragment(
                            Heading(
                                "Gateway worker",
                                Paragraph("Link an external webhook to control various events that occur in your AI gateway."),
                                Paragraph("Your worker functions as middleware between an event and the final action of that event, allowing you to abort or control that event externally.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayWorkerScriptSource",
                                        value: gateway.value?.parameters.workerScriptSource,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "https://example.com/hook", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.startsWith("http://") || x.startsWith("https://"), "The worker address must be a valid URL."]
                                                ],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Worker address"),
                                        Paragraph("Specify the external gateway worker URL.")
                                    )
                                ),
                            )
                        )
                    }
                ], {
                    type: "fixed"
                })
            )
        )
    );
}
